import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { API_BASE_URL } from '../config/api';
import { showToast } from '../utils/toast';
import './Sabloni.css';

function Sabloni() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('meals');

  const [mealTemplates, setMealTemplates] = useState([]);
  const [workoutTemplates, setWorkoutTemplates] = useState([]);
  const [mealExpanded, setMealExpanded] = useState(null);
  const [workoutExpanded, setWorkoutExpanded] = useState(null);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchMealTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/templates/meals`, { headers: getAuthHeader() });
      setMealTemplates(res.data);
    } catch {
      // silently ignore
    }
  }, [getAuthHeader]);

  const fetchWorkoutTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/templates/workouts`, { headers: getAuthHeader() });
      setWorkoutTemplates(res.data);
    } catch {
      // silently ignore
    }
  }, [getAuthHeader]);

  const [suppTemplates, setSuppTemplates] = useState([]);
  const [suppExpanded, setSuppExpanded] = useState(null);

  const fetchSuppTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/templates/supplements`, { headers: getAuthHeader() });
      setSuppTemplates(res.data || []);
    } catch {
      // silently ignore
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchMealTemplates();
    fetchWorkoutTemplates();
    fetchSuppTemplates();
  }, [fetchMealTemplates, fetchWorkoutTemplates, fetchSuppTemplates]);

  const handleDeleteSupp = async (id) => {
    if (!window.confirm(t('templates.confirmDelete'))) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/templates/supplements/${id}`, { headers: getAuthHeader() });
      showToast.success(t('templates.deletedSuccess'));
      fetchSuppTemplates();
    } catch {
      showToast.error('Error deleting template');
    }
  };

  const handleDeleteMeal = async (id) => {
    if (!window.confirm(t('templates.confirmDelete'))) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/templates/meals/${id}`, { headers: getAuthHeader() });
      showToast.success(t('templates.deletedSuccess'));
      fetchMealTemplates();
    } catch {
      showToast.error('Error deleting template');
    }
  };

  const handleDeleteWorkout = async (id) => {
    if (!window.confirm(t('templates.confirmDelete'))) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/templates/workouts/${id}`, { headers: getAuthHeader() });
      showToast.success(t('templates.deletedSuccess'));
      fetchWorkoutTemplates();
    } catch {
      showToast.error('Error deleting template');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const renderTemplateList = (templates, expanded, setExpanded, onDelete, countLabel) => {
    if (templates.length === 0) {
      return <p className="no-templates">{t('templates.noTemplates')}</p>;
    }
    return (
      <ul className="template-list">
        {templates.map((tmpl) => (
          <li key={tmpl.id} className="template-item">
            <div
              className="template-item-header"
              onClick={() => setExpanded(expanded === tmpl.id ? null : tmpl.id)}
            >
              <div className="template-item-info">
                <span className="template-item-name">{tmpl.name}</span>
                <span className="template-item-meta">
                  {tmpl.meal_count || tmpl.workout_count || (tmpl.data?.supplements?.length ?? '')} {countLabel}
                  {tmpl.created_at && <> &middot; {formatDate(tmpl.created_at)}</>}
                </span>
              </div>
              <div className="template-item-actions">
                <button
                  className="template-delete-btn"
                  onClick={(e) => { e.stopPropagation(); onDelete(tmpl.id); }}
                >
                  {t('templates.deleteTemplate')}
                </button>
                <span className="template-chevron">{expanded === tmpl.id ? '▲' : '▼'}</span>
              </div>
            </div>
            {expanded === tmpl.id && (
              <div className="template-preview">
                <pre>{JSON.stringify(tmpl.data, null, 2)}</pre>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="sabloni-container">
      <div className="content-wrapper">
        <div className="sabloni-header">
          <h2>{t('templates.title')}</h2>
          <p className="sabloni-hint">{t('templates.hint')}</p>
        </div>

        <div className="sabloni-tabs">
          <button
            className={`tab-btn ${activeTab === 'meals' ? 'active' : ''}`}
            onClick={() => setActiveTab('meals')}
          >
            {t('templates.tabMeals')}
            {mealTemplates.length > 0 && (
              <span className="tab-count">{mealTemplates.length}</span>
            )}
          </button>
          <button
            className={`tab-btn ${activeTab === 'workouts' ? 'active' : ''}`}
            onClick={() => setActiveTab('workouts')}
          >
            {t('templates.tabWorkouts')}
            {workoutTemplates.length > 0 && (
              <span className="tab-count">{workoutTemplates.length}</span>
            )}
          </button>
          <button
            className={`tab-btn ${activeTab === 'supplements' ? 'active' : ''}`}
            onClick={() => setActiveTab('supplements')}
          >
            {t('templates.tabSupplements')}
            {suppTemplates.length > 0 && (
              <span className="tab-count">{suppTemplates.length}</span>
            )}
          </button>
        </div>

        {activeTab === 'supplements' && (
          <div className="sabloni-panel">
            <div className="template-list-card">
              <h3>{t('templates.supplementTemplates')}</h3>
              {renderTemplateList(
                suppTemplates,
                suppExpanded,
                setSuppExpanded,
                handleDeleteSupp,
                t('templates.supplementsLabel')
              )}
            </div>
          </div>
        )}

        {activeTab === 'meals' && (
          <div className="sabloni-panel">
            <div className="template-list-card">
              <h3>{t('templates.mealTemplates')}</h3>
              {renderTemplateList(
                mealTemplates,
                mealExpanded,
                setMealExpanded,
                handleDeleteMeal,
                t('templates.mealsLabel')
              )}
            </div>
          </div>
        )}

        {activeTab === 'workouts' && (
          <div className="sabloni-panel">
            <div className="template-list-card">
              <h3>{t('templates.workoutTemplates')}</h3>
              {renderTemplateList(
                workoutTemplates,
                workoutExpanded,
                setWorkoutExpanded,
                handleDeleteWorkout,
                t('templates.workoutsLabel')
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sabloni;
